import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { type Column } from './DataTable';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge, { type StatusBadgeVariant } from './ui/StatusBadge';
import { RecordIdLabel } from './ui/idColumn';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useLoad } from '../hooks/useLoad';
import { useMoney } from '../hooks/useMoney';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { useRole } from '../hooks/useRole';
import { setAsoebiOrdersBar, useAsoebiOrdersBar } from '../hooks/useAsoebiOrdersBar';
import { refreshAssignmentsFromApi } from '../services/AsoebiService';
import { filterOrderRows, summarizeOrders, toOrderListRow, type OrderListRow } from '../utils/asoebiOrdersList';
import { ASOEBI_ORDER_NEW_PATH, ASOEBI_ORDERS_PATH } from '../utils/asoebiOrdersRoute';
import { formatCurrencyTotals, currencyTotalLines, formatDecimal, isNonZero } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDate } from '../utils/formatDate';
import { idSortKey } from './ui/idColumn';
import { recordLabel } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';
import { ApiError } from '../utils/apiErrors';
import type { NamedRow } from '../utils/fabricRef';
import '../styles/components/AsoebiOrdersList.css';

const STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  pending_confirmation: 'warning',
  ordered: 'info',
  paid: 'success',
  delivered: 'success',
  collected: 'success',
  cancelled: 'neutral',
  returned: 'danger',
};
const PAYMENT_VARIANT: Record<string, StatusBadgeVariant> = { unpaid: 'danger', partial: 'warning', paid: 'success' };

/** Row fields that hold or reveal money, left out of the header search for someone who may not see money. */
const MONEY_FIELDS = ['total_price', 'paid', 'balance', 'payment_status', 'payment_label'] as const;

const orderPath = (row: OrderListRow): string => entityPath('fabricAssignment', row.id) ?? ASOEBI_ORDERS_PATH;

interface AsoebiOrdersListProps {
  /** The fabric and bundle catalogues, to name what each order is for. */
  fabrics: readonly NamedRow[];
  bundles: readonly NamedRow[];
}

const OrdersSkeleton: React.FC = () => (
  <div className="asoebi-orders__skeleton" role="status" aria-busy="true" aria-label="Loading aso-ebi orders">
    <div className="asoebi-orders__skeleton-cards" aria-hidden="true">
      {[0, 1, 2, 3].map(i => <div key={i} className="skeleton asoebi-orders__skeleton-card" />)}
    </div>
    <div className="asoebi-orders__skeleton-rows" aria-hidden="true">
      {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton asoebi-orders__skeleton-row" />)}
    </div>
  </div>
);

/**
 * The Orders tab: every guest aso-ebi order of the project (GET asoebi/orders)
 * as a sortable table, with summary cards. Each row is a real link to the
 * order's own page. Filters and the Archived switch live in the bar
 * (AsoebiOrdersBar); search is the global header search; Refresh is the
 * global header's. Money columns and cards are absent for a role that may
 * not see money, and a viewer gets no New order.
 */
const AsoebiOrdersList: React.FC<AsoebiOrdersListProps> = ({ fabrics, bundles }) => {
  const navigate = useNavigate();
  const { canEdit, canViewFinancials } = useRole();
  const { currency: projectCurrency } = useMoney();
  const bar = useAsoebiOrdersBar();
  const search = useHeaderSearch();
  const { version: refsVersion } = useRecordRefs();

  const { data, loading, error, reload } = useLoad(() => refreshAssignmentsFromApi(bar.archived), [bar.archived]);
  usePageRefresh(reload, loading);

  const rows = useMemo(
    () => (data ?? []).map(a => toOrderListRow(a, {
      fabrics, bundles, refLabel: id => recordLabel('fabricAssignment', id), fallbackCurrency: projectCurrency,
    })),
    // refsVersion: record numbers arriving change the labels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, fabrics, bundles, projectCurrency, refsVersion],
  );
  const shown = useMemo(
    () => filterOrderRows(rows, { status: bar.status, payment: bar.payment, search, canViewFinancials }),
    [rows, bar.status, bar.payment, search, canViewFinancials],
  );
  const summary = useMemo(() => summarizeOrders(shown), [shown]);

  if (error) {
    const forbidden = error instanceof ApiError && error.status === 403;
    return (
      <div className="asoebi-orders">
        {forbidden ? (
          <EmptyState icon="lock" title="You don't have access to aso-ebi orders" description="Ask the project owner to change your role if you need them." />
        ) : (
          <LoadErrorBox what="aso-ebi orders" error={error} onRetry={reload} retrying={loading} />
        )}
      </div>
    );
  }

  if (loading && data === null) {
    return <div className="asoebi-orders"><OrdersSkeleton /></div>;
  }

  const activeFilters = Boolean(bar.status || (canViewFinancials && bar.payment));
  const filtering = activeFilters || Boolean(search.trim());
  const newOrderAction = canEdit && !bar.archived
    ? <button type="button" className="primary-btn" onClick={() => navigate(ASOEBI_ORDER_NEW_PATH)}>New order</button>
    : undefined;

  const idCell: Column<OrderListRow> = {
    key: 'id', header: 'ID', width: '112px', sortable: true, className: 'rec-id-cell',
    sortValue: r => idSortKey('fabricAssignment', r.id),
    render: (_v, r) => (
      <Link to={orderPath(r)} className="asoebi-orders__link" onClick={e => e.stopPropagation()} aria-label={`Open order ${r.ref}`}>
        <RecordIdLabel kind="fabricAssignment" id={r.id} />
      </Link>
    ),
  };

  const money = (value: string | null, currency: string | null) => formatDecimal(value, currency);
  const columns: Column<OrderListRow>[] = [
    idCell,
    {
      key: 'guest_name', header: 'Guest', sortable: true,
      render: (_v, r) => (
        <>
          <Link to={orderPath(r)} className="asoebi-orders__link" onClick={e => e.stopPropagation()}>{r.guest_name}</Link>
          {r.guest_phone && <span className="asoebi-orders__sub">{r.guest_phone}</span>}
        </>
      ),
    },
    { key: 'fabric_name', header: 'Fabric / package', sortable: true, render: (_v, r) => <span className="asoebi-orders__item">{r.fabric_name}</span> },
    {
      key: 'quantity', header: 'Qty', sortable: true, align: 'right',
      sortValue: r => Number(r.quantity),
      render: (_v, r) => <span className="asoebi-orders__num">{r.quantity} {r.unit}{r.quantity === '1' ? '' : 's'}</span>,
    },
    {
      key: 'status', header: 'Status', sortable: true,
      render: (_v, r) => <StatusBadge variant={STATUS_VARIANT[r.status] ?? 'neutral'}>{r.status_label}</StatusBadge>,
    },
    // Money columns are left out entirely for someone who may not see money.
    ...(canViewFinancials ? [
      {
        key: 'payment_status', header: 'Payment', sortable: true,
        render: (_v: unknown, r: OrderListRow) => <StatusBadge variant={PAYMENT_VARIANT[r.payment_status] ?? 'neutral'}>{r.payment_label}</StatusBadge>,
      },
      { key: 'total_price', header: 'Price', sortable: true, align: 'right' as const, sortValue: (r: OrderListRow) => Number(r.total_price ?? 0), render: (_v: unknown, r: OrderListRow) => <span className="asoebi-orders__num">{money(r.total_price, r.currency)}</span> },
      { key: 'paid', header: 'Paid', sortable: true, align: 'right' as const, sortValue: (r: OrderListRow) => Number(r.paid ?? 0), render: (_v: unknown, r: OrderListRow) => <span className="asoebi-orders__num">{money(r.paid, r.currency)}</span> },
      {
        key: 'balance', header: 'Balance', sortable: true, align: 'right' as const, sortValue: (r: OrderListRow) => Number(r.balance ?? 0),
        render: (_v: unknown, r: OrderListRow) => (
          <span className={`asoebi-orders__num${isNonZero(r.balance) ? ' asoebi-orders__balance--due' : ''}`}>{money(r.balance, r.currency)}</span>
        ),
      },
    ] : []),
    { key: 'created_at', header: 'Created', sortable: true, render: (_v, r) => formatDate(r.created_at) },
  ];

  return (
    <div className="asoebi-orders">
      

      <section className="stats-grid" aria-label="Orders summary">
        <MetricCard label={bar.archived ? 'Archived orders' : 'Orders'} value={summary.count} tone="pink" icon="list" />
        {canViewFinancials && (
          <>
            <MetricCard label="Total value" value={formatCurrencyTotals(summary.value)} lines={currencyTotalLines(summary.value)} tone="blue" icon="chart" />
            <MetricCard label="Collected" value={formatCurrencyTotals(summary.collected)} lines={currencyTotalLines(summary.collected)} tone="green" icon="check" />
            <MetricCard label="Outstanding" value={formatCurrencyTotals(summary.outstanding)} lines={currencyTotalLines(summary.outstanding)} tone="orange" icon="wallet" />
          </>
        )}
      </section>

      {rows.length === 0 ? (
        <EmptyState
          icon="list"
          title={bar.archived ? 'No archived aso-ebi orders' : 'No aso-ebi orders yet'}
          description={bar.archived ? undefined : 'When a guest orders fabric or a package, their order appears here.'}
          action={newOrderAction}
        />
      ) : shown.length === 0 ? (
        <EmptyState
          icon="search"
          title="No orders match"
          description={filtering ? 'Try a different search, or clear the filters.' : undefined}
          action={activeFilters
            ? <button type="button" className="secondary-btn" onClick={() => setAsoebiOrdersBar({ status: '', payment: '' })}>Clear filters</button>
            : undefined}
        />
      ) : (
        <DataTable<OrderListRow>
          recordList={{ kind: 'fabricAssignment' }}
          data={shown}
          columns={columns}
          emptyMessage="No orders"
          filterOmit={canViewFinancials ? undefined : MONEY_FIELDS}
          pagination={{ enabled: true, pageSize: 25 }}
          defaultSort={{ key: 'created_at', direction: 'desc' }}
          onRowClick={r => navigate(orderPath(r))}
        />
      )}
    </div>
  );
};

export default AsoebiOrdersList;
